import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Bike, CheckCircle2, Search, MapPin, ThumbsUp, Route } from "lucide-react";
import Header from "@/components/Header";
import LiveMap from "@/components/LiveMap";
import { api } from "@/lib/api";
import { useLang } from "@/i18n";

const T = {
  en: { title: "Track your CHELA", finding: "Payment received. Finding your CHELA…", accepted: "has accepted your booking", on_the_way: "is on the way", reached: "has reached your location", done: "Job done. Thank you!", unpaid: "Payment pending. Your booking is not confirmed yet.", away: "km away", eta: (m) => `Arriving in about ${m} minutes`, etaNote: "Estimate based on Bhopal city traffic", wait: "Location will appear once your CHELA starts moving.", bad: "This tracking link is not valid.", steps: ["Paid", "Accepted", "On the way", "Reached", "Done"], travel: (km, a) => `Travel charge: ${km} km = ₹${a}`, payTravel: "Pay travel charge", auto: "This page updates by itself." },
  hi: { title: "अपने CHELA को ट्रैक करें", finding: "पेमेंट मिल गया। आपका CHELA ढूँढा जा रहा है…", accepted: "ने आपकी बुकिंग स्वीकार कर ली है", on_the_way: "रास्ते में है", reached: "आपकी लोकेशन पर पहुँच गया है", done: "काम पूरा हुआ। धन्यवाद!", unpaid: "पेमेंट बाकी है। बुकिंग अभी कन्फ़र्म नहीं है।", away: "किमी दूर", eta: (m) => `लगभग ${m} मिनट में पहुँच रहा है`, etaNote: "भोपाल के ट्रैफ़िक के हिसाब से अनुमान", wait: "CHELA के निकलते ही लोकेशन दिखेगी।", bad: "यह ट्रैकिंग लिंक सही नहीं है।", steps: ["पेमेंट", "स्वीकार", "रास्ते में", "पहुँचा", "पूरा"], travel: (km, a) => `ट्रैवल चार्ज: ${km} किमी = ₹${a}`, payTravel: "ट्रैवल चार्ज दें", auto: "यह पेज अपने आप अपडेट होता है।" },
};
const ORDER = ["finding", "accepted", "on_the_way", "reached", "done"];

function Steps({ c, status }) {
  const at = ORDER.indexOf(status);
  return (
    <div data-testid="track-steps" className="chela-card rounded-3xl p-5 grid grid-cols-5 gap-1" style={{ transform: "none" }}>
      {c.steps.map((s, i) => (
        <div key={s} data-testid={`track-step-${i}`} className="flex flex-col items-center text-center gap-1.5">
          <span className={`h-8 w-8 rounded-full flex items-center justify-center text-sm font-bold ${i <= at ? "bg-[#1B8A4B] text-white" : "bg-[#E5E0D8] text-[#8A8D95]"} ${i === at && status !== "done" ? "ring-4 ring-[#1B8A4B]/25 animate-pulse" : ""}`}>{i <= at ? <CheckCircle2 size={16} /> : i + 1}</span>
          <span className={`text-[11px] sm:text-xs font-semibold leading-tight ${i <= at ? "text-[#121316]" : "text-[#8A8D95]"}`}>{s}</span>
        </div>
      ))}
    </div>
  );
}
const ICON = { finding: Search, accepted: ThumbsUp, on_the_way: Bike, reached: MapPin, done: CheckCircle2, unpaid: Search };

export default function Track() {
  const { token } = useParams();
  const { lang } = useLang();
  const c = T[lang] || T.en;
  const [d, setD] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    const load = () => api.get(`/track/${token}`).then((r) => setD(r.data)).catch(() => setErr(true));
    load();
    const iv = setInterval(load, 15000);
    return () => clearInterval(iv);
  }, [token]);

  const Icon = d ? ICON[d.status] : Search;
  const who = d?.chela ? `${d.chela} (CHELA)` : "CHELA";
  const line = d && (d.status === "accepted" || ["on_the_way", "reached"].includes(d.status) ? `${who} ${c[d.status]}` : c[d.status]);
  return (
    <div className="min-h-screen bg-[#FAF8F5]">
      <Header />
      <div className="chela-container max-w-lg py-8">
        <h1 className="font-display font-extrabold text-3xl text-[#121316]">{c.title}</h1>
        {err && <p data-testid="track-bad" className="mt-6 text-[#5A5D65]">{c.bad}</p>}
        {d && (
          <div className="mt-5 space-y-4">
            {d.status !== "unpaid" && <Steps c={c} status={d.status} />}
            <div data-testid="track-status" className="chela-card rounded-3xl p-5 flex items-center gap-4" style={{ transform: "none" }}>
              <span className="h-12 w-12 rounded-2xl bg-[#FFF1EC] flex items-center justify-center"><Icon size={24} className="text-[#E05624]" /></span>
              <div>
                <div className="font-display font-bold text-lg text-[#121316]">{line}</div>
                <div className="text-sm text-[#5A5D65]">{d.service} · {d.date} {d.time} · #{d.ref}{d.km != null && <b className="text-[#E05624]"> · {d.km} {c.away}</b>}</div>
              </div>
            </div>
            {d.status === "on_the_way" && d.eta_min != null && (
              <div data-testid="track-eta" className="rounded-3xl bg-[#E05624] text-white p-5">
                <div className="font-display font-extrabold text-2xl">{c.eta(d.eta_min)}</div>
                <div className="text-sm text-white/80">{c.etaNote}</div>
              </div>
            )}
            {d.status === "on_the_way" && (d.loc ? <LiveMap loc={d.loc} testid="track-map" /> : <p className="text-sm text-[#5A5D65]">{c.wait}</p>)}
            {d.travel && (
              <div data-testid="track-travel" className="rounded-3xl bg-[#FFF1EC] border border-[#F6D9CD] p-5">
                <div className="font-bold text-[#121316] flex items-center gap-2"><Route size={18} className="text-[#E05624]" /> {c.travel(d.travel.km, d.travel.amount)}</div>
                <Link data-testid="track-travel-pay" to={`/travel/${d.travel.token}`} className="mt-3 w-full h-12 rounded-full btn-saffron font-bold inline-flex items-center justify-center">{c.payTravel}</Link>
              </div>
            )}
            <p className="text-xs text-center text-[#8A8D95]">{c.auto}</p>
          </div>
        )}
      </div>
    </div>
  );
}
